import {
  InstagramIcon,
  LinkedinIcon,
  LogoWhiteImage,
  TwitterIcon,
  YoutubeIcon,
} from "assets";
import classNames from "classnames";
import { links } from "data";
import { AnchorProps, WithChildren } from "types";
import styles from "./footer.module.scss";

export const Footer = () => {
  return (
    <footer className={styles.footer}>
      <div className="container-fluid">
        <TopSection />
        <BottomSection />
      </div>
    </footer>
  );
};

const TopSection = () => (
  <div className="row mb-xxl">
    <LinksColumn>
      <FooterLink href={links.ABOUT_US} subtitle>
        About us
      </FooterLink>
      <FooterLink href={links.AWARDS}>Awards</FooterLink>
      <FooterLink href={links.CASE_STUDIES}>Case studies</FooterLink>
      <FooterLink href={links.NATIONAL_COVERAGE}>
        Nationwide coverage
      </FooterLink>
      <FooterLink href={links.COVID_SECURE}>COVID-19 secure</FooterLink>
    </LinksColumn>
    <LinksColumn>
      <span className={styles.subtitle}>Work with us</span>
      <FooterLink href={links.CAREERS}>Careers</FooterLink>
      <FooterLink href={links.SELL_YOUR_BUSINESS}>
        Sell your business
      </FooterLink>
      <FooterLink href={links.BECOME_A_COURIER}>Become a courier</FooterLink>
    </LinksColumn>
    <LinksColumn>
      <FooterLink href={links.SUPPORT} subtitle>
        Support
      </FooterLink>
      <FooterLink href={links.CONTACT_US}>Contact us</FooterLink>
      <FooterLink href={links.GUIDES_AND_RESOURCES}>
        Guides and resources
      </FooterLink>
      <FooterLink href={links.SIGN_UP_FOR_AN_ACCOUNT}>
        Sign up with CitySprint
      </FooterLink>
      <FooterLink href={links.REQUEST_AN_ONLINE_PROFILE}>
        Activate your online account
      </FooterLink>
      <FooterLink href={links.FIND_YOUR_LOCAL_SERVICE_CENTRE}>
        Locations
      </FooterLink>
    </LinksColumn>
    <LinksColumn>
      <FooterLink href={links.INSIGHTS} subtitle>
        Insights
      </FooterLink>
      <FooterLink href={links.NEWS}>News</FooterLink>
      <FooterLink href={links.BLOG}>Blog</FooterLink>
    </LinksColumn>
    <LinksColumn>
      <FooterLink href={links.PAYMENT_OPTIONS} subtitle>
        Payments and charges
      </FooterLink>
      <FooterLink href={links.GLOBAL_ENERGY_SURCHARGE}>Global energy surcharge</FooterLink>
      <FooterLink href={links.FUEL_SURCHARGES}>Fuel surcharges</FooterLink>
      <FooterLink href={links.INDEMNITY_REQUEST}>Indemnity request</FooterLink>
      <FooterLink href={links.PUBLIC_LIABILITY_INSURANCE}>
        Public liability insurance
      </FooterLink>
    </LinksColumn>
    <LinksColumn>
      <div className="flex justify-content-md-end gap-st">
        <FooterLink href={links.CS_INSTAGRAM}>
          <InstagramIcon />
        </FooterLink>
        <FooterLink href={links.CS_TWITTER}>
          <TwitterIcon />
        </FooterLink>
        <FooterLink href={links.CS_LINKEDIN}>
          <LinkedinIcon />
        </FooterLink>
        <FooterLink href={links.CS_YOUTUBE}>
          <YoutubeIcon />
        </FooterLink>
      </div>
    </LinksColumn>
  </div>
);

const BottomSection = () => (
  <div className="row">
    <div className="col-md-6">
      <a href={links.HOMEPAGE}>
        <LogoWhiteImage title="CitySprint footer logo" />
      </a>
      <h4 className="mt-xl">Delivering what matters.</h4>
    </div>
    <div className="col-md-6 pt-st">
      <div className="flex flex-column h-100 align-items-md-end justify-content-end font-md">
        <div>Copyright © {new Date().getFullYear()} CitySprint (UK) Ltd</div>
        <div>
          <FooterLink href={links.GDPR}>GDPR</FooterLink>
          {" | "}
          <FooterLink href={links.TERMS_AND_CONDITIONS}>
            Terms and conditions
          </FooterLink>
          {" | "}
          <FooterLink href={links.COOKIES}>Cookies policy</FooterLink>
          {" | "}
          <FooterLink href={links.MODERN_SLAVERY_ACT}>
            Modern slavery statement
          </FooterLink>
          {" | "}
          <FooterLink href={links.PRIVACY_POLICY}>Privacy policy</FooterLink>
        </div>
      </div>
    </div>
  </div>
);

interface FooterLinkProps extends AnchorProps {
  subtitle?: boolean;
}

const FooterLink = ({
  children,
  subtitle,
  className,
  ...props
}: FooterLinkProps) => (
  <a
    {...props}
    className={classNames(styles.link, subtitle && styles.subtitle, className)}
  >
    {children}
  </a>
);

const LinksColumn = ({ children }: WithChildren) => (
  <div className="col-md-2 flex flex-column gap-xxs mb-st">{children}</div>
);
